import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CommonConstants } from './shared/constants/common.constants';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { SidebarService } from './shared/services/common/sidebar.service';
import { CommonModule } from '@angular/common';
import { SidebarEnum } from './shared/models/common/sidebar.enum';
import { AuthService } from './shared/services/common/auth.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { UserModel } from './shared/models/apis/system/user/user.model';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: true,
    imports: [
        RouterOutlet,
        ToastModule,
        NavbarComponent,
        SidebarComponent,
        FooterComponent,
        CommonModule,
        ProgressSpinnerModule
    ],
})
export class AppComponent implements OnInit {

    private readonly _authService = inject(AuthService);
    private readonly _messageService = inject(MessageService);
    private readonly _sidebarService = inject(SidebarService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _eventService = inject(PublicEventsService);
    private readonly _oidcSecurityService = inject(OidcSecurityService);


    minWidth: number = 800;
    isMobileState: boolean = innerWidth<this.minWidth;
    sidebarState: SidebarEnum = this.isMobileState?SidebarEnum.mobileCollapsed:SidebarEnum.desktopLarge;
    desktopLargeMinWidth: number = 990;
    authLoading: boolean = false;
    currentUser: UserModel|null = null;

    showLayout: boolean = false;

    constructor(
        private primengConfig: PrimeNGConfig,
        private router: Router
    ) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const hiddenRoutes = ['/welcome', '/unauthorized'];
                const hiddenPrefixes = ['/public/kitchen-tv-dashboard', '/public/tv-dashboard'];
                this.showLayout = !hiddenRoutes.includes(event.urlAfterRedirects)
                    && !hiddenPrefixes.some(prefix => event.urlAfterRedirects.startsWith(prefix));
                if(this.showLayout){
                    this.authLoading = true;
                    this._authService.initAuth().then(async() => {
                        this.currentUser = await this._authService.getCurrentUser();
                        console.log("Checking user last token for user");
                        await this._authService.checkUserLastToken(this.currentUser?.lastToken);
                        this.sidebarStateObserver();
                        this.resizeObserver();
                        this.setAndDetectResolution();
                    })
                    .finally(() => {
                        this.authLoading = false;
                    });

                    this._authService.userAccountObservable.subscribe(user => {
                        this.currentUser = user;
                        this.setAndDetectResolution();
                    });
                    this.oidcEventsLogger();
                }
            });


    }

    public get globalErrorHandlerToastKey(): string {
        return CommonConstants.globalErrorHandlerToastKey;
    }

    ngOnInit() {
        this.primengConfig.ripple = true;
        if (self !== top) { top!.location.href = self.location.href; }
    }

    oidcEventsLogger(){
        this._eventService
            .registerForEvents()
            .subscribe((notification) => {
                console.log("OIDC Event Trigger");
                console.log(notification);
                if(notification && notification.type){
                    switch(<number>notification.type){
                        case EventTypes.ConfigLoaded :{
                           console.info('EventTypes.ConfigLoaded');
                           break;
                        }
                        case EventTypes.CheckingAuth :{
                            console.info('EventTypes.CheckingAuth');
                            break;
                         }
                         case EventTypes.CheckingAuthFinished :{
                            console.info('EventTypes.CheckingAuthFinished');
                            break;
                         }
                         case EventTypes.CheckingAuthFinishedWithError :{
                            console.info('EventTypes.CheckingAuthFinishedWithError');
                            break;
                         }
                         case EventTypes.ConfigLoadingFailed :{
                            console.info('EventTypes.ConfigLoadingFailed');
                            break;
                         }
                         case EventTypes.CheckSessionReceived :{
                            console.info('EventTypes.CheckSessionReceived');
                            break;
                         }
                         case EventTypes.UserDataChanged :{
                            console.info('EventTypes.UserDataChanged');
                            break;
                         }
                         case EventTypes.NewAuthenticationResult :{
                            console.info('EventTypes.NewAuthenticationResult');
                            break;
                         }
                         case EventTypes.TokenExpired :{
                            console.info('EventTypes.TokenExpired');
                            break;
                         }
                         case EventTypes.IdTokenExpired :{
                            console.info('EventTypes.IdTokenExpired');
                            break;
                         }
                         case EventTypes.SilentRenewStarted :{
                            console.info('EventTypes.SilentRenewStarted');
                            break;
                         }
                         case EventTypes.SilentRenewFailed :{
                            console.info('EventTypes.SilentRenewFailed');
                            break;
                         }
                         default :{
                            console.info('EventTypes.Unknown');
                            break;
                         }
                    }
                }
            });
    }

    sidebarStateObserver(){
        this._sidebarService.sidebarStateObservable.subscribe({
            next: (value) => {
                this.sidebarState = value;
                this._changeDetectorRef.detectChanges();

            },
        });
    }

    resizeObserver() {
        const resizeObserver = new ResizeObserver((entries) => {

            this.setAndDetectResolution();
        });

        resizeObserver.observe(window.document.body);
    }

    setAndDetectResolution(){
        if (innerWidth < this.minWidth) {
            this.isMobileState = true;
            this._sidebarService.setState(SidebarEnum.mobileCollapsed);

        } else {
            if(innerWidth < this.desktopLargeMinWidth){
                this._sidebarService.setState(SidebarEnum.desktopSmall);
            } else if(innerWidth>= this.desktopLargeMinWidth) {
                if(this.currentUser && this.currentUser.userSetting && this.currentUser.userSetting.isSidebarCollapsedOnPageLoad){
                    this._sidebarService.setState(SidebarEnum.desktopSmall);
                } else {
                    this._sidebarService.setState(SidebarEnum.desktopLarge);

                }
            }
            this.isMobileState = false;
        }

        this._sidebarService.setMobileState(this.isMobileState);
        this._changeDetectorRef.detectChanges();
    }

    closeToast() {
        this._messageService.clear(this.globalErrorHandlerToastKey);
    }

    public get sidebarSize() {

        let size = '270px 1fr';
        if(this.isMobileState){
            size = '0px 1fr';
        } else {
            switch (this.sidebarState) {
                case SidebarEnum.desktopLarge: {
                    size = '270px 1fr';
                    break;
                }
                case SidebarEnum.desktopSmall: {
                    size = '80px 1fr';
                    break;
                }
                default: {
                    size = '270px 1fr';
                    break;
                }
            }
        }

        return {
            'grid-template-columns': size,
        };
    }
}
